import type { Skill } from '@/types';

/**
 * Source unique pour les libellés et le regroupement des compétences.
 *
 * Demande du boss : les 10 compétences alignées à plat étaient illisibles sur la fiche
 * salarié et dans la liste. Elles sont désormais regroupées en 3 familles métier.
 *
 * NB : `AppointmentsPage.tsx` garde sa propre table de libellés (plus courts, calibrés
 * pour la grille des RDV). Cette feature-là est validée en live, on n'y touche pas.
 */

export const SKILL_LABELS: Record<Skill, string> = {
  COMPTOIR: 'Comptoir',
  VACCINATION: 'Vaccination',
  ENTRETIEN: 'Entretien pharmaceutique',
  BILAN: 'Bilan prévention',
  PREPARATION: 'Préparation',
  BACKOFFICE_PHARMA: 'Back office pharmacie',
  BACKOFFICE_TECHNIQUE: 'Back office technique',
  TROD: 'TROD',
  PDA: 'PDA',
  PREPARATOIRE: 'Préparatoire',
};

export const SKILL_DESCRIPTIONS: Record<Skill, string> = {
  COMPTOIR: 'Accueil et vente au comptoir',
  VACCINATION: 'Autorisé·e à réaliser des vaccinations',
  ENTRETIEN: 'Entretiens pharmaceutiques patients chroniques',
  BILAN: 'Bilans partagés de médication',
  PREPARATION: 'Préparations magistrales',
  BACKOFFICE_PHARMA: 'Gestion arrière pharmacie (délivrance, stupéfiants)',
  BACKOFFICE_TECHNIQUE: 'Gestion arrière technique (stocks, commandes)',
  TROD: "Tests rapides d'orientation diagnostique",
  PDA: 'Préparation de doses à administrer',
  PREPARATOIRE: 'Travail au préparatoire',
};

export type SkillFamilyId = 'SOINS' | 'TECHNIQUE' | 'ACCUEIL';

export interface SkillFamily {
  id: SkillFamilyId;
  label: string;
  /** Ce que la famille recouvre — affiché en sous-titre sur la fiche salarié. */
  description: string;
  skills: Skill[];
  /** Classes Tailwind de la pastille compacte utilisée dans la liste des salariés. */
  chip: string;
  dot: string;
}

/**
 * Les 3 familles demandées. L'ordre fait foi pour l'affichage.
 * Toute compétence doit appartenir à exactement une famille (garanti par `familyOf`).
 */
export const SKILL_FAMILIES: SkillFamily[] = [
  {
    id: 'SOINS',
    label: 'Soins',
    description: 'Actes réalisés auprès du patient',
    skills: ['VACCINATION', 'TROD', 'ENTRETIEN', 'BILAN'],
    chip: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    dot: '#059669',
  },
  {
    id: 'TECHNIQUE',
    label: 'Technique',
    description: 'Préparation et gestion en arrière-boutique',
    skills: ['PREPARATION', 'PDA', 'PREPARATOIRE', 'BACKOFFICE_PHARMA', 'BACKOFFICE_TECHNIQUE'],
    chip: 'bg-violet-50 text-violet-800 border-violet-200',
    dot: '#7c3aed',
  },
  {
    id: 'ACCUEIL',
    label: 'Accueil',
    description: 'Contact direct avec la patientèle',
    skills: ['COMPTOIR'],
    chip: 'bg-sky-50 text-sky-800 border-sky-200',
    dot: '#0284c7',
  },
];

const FAMILY_BY_SKILL = new Map<Skill, SkillFamily>(
  SKILL_FAMILIES.flatMap((f) => f.skills.map((s) => [s, f] as const)),
);

export function familyOf(skill: Skill): SkillFamily | undefined {
  return FAMILY_BY_SKILL.get(skill);
}

/**
 * Répartit les compétences d'un salarié par famille, en conservant l'ordre de
 * `SKILL_FAMILIES`. Les familles vides sont omises — la liste doit rester compacte.
 */
export function groupSkillsByFamily(skills: Skill[]): Array<{ family: SkillFamily; skills: Skill[] }> {
  const owned = new Set(skills);
  return SKILL_FAMILIES.map((family) => ({
    family,
    skills: family.skills.filter((s) => owned.has(s)),
  })).filter((g) => g.skills.length > 0);
}
